import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {createSafeIndexedDbPersister, SIMPLE_VALIDATOR} from '@github-ui/safe-indexed-db-storage'
import {useEffect, useRef, useState} from 'react'

const ISSUE_SHOW_PATTERN = /^https?:\/\/[^/]+\/([^/]+)\/([^/]+)\/issues\/(\d+)(?:\?[^#]*)?(?:#.*)?$/

export function usePreheatIssues(): void {
  const issuesRef = useRef<Record<string, boolean>>({})
  const pendingRef = useRef(false)
  const [storage] = useState<ReturnType<typeof createSafeIndexedDbPersister> | null>(() =>
    createSafeIndexedDbPersister({
      validator: SIMPLE_VALIDATOR,
      sendAnalyticsEvent: (eventType, target?, payload = {}) => {
        sendEvent(eventType, {...payload, target})
      },
    }),
  )

  const enabled =
    !isFeatureEnabled('disable_preheating_for_issues') && isFeatureEnabled('issues_react_preheating_pulls')

  useEffect(() => {
    if (!enabled) {
      return
    }
    let cancelled = false

    const addIssue = (href: string) => {
      if (!ISSUE_SHOW_PATTERN.test(href)) return
      if (href in issuesRef.current) return
      issuesRef.current[href] = true
      pendingRef.current = true
      scheduleNextPreheat()
    }

    const preheatQueries = async () => {
      const issuesToPreheat = Object.keys(issuesRef.current).filter(href => issuesRef.current[href])
      if (issuesToPreheat.length === 0) {
        pendingRef.current = false
        return
      }
      for (const href of issuesToPreheat) {
        issuesRef.current[href] = false
      }
      let noIssuesToPreheat = true
      for (const href of issuesToPreheat) {
        try {
          const urlMatch = href.match(ISSUE_SHOW_PATTERN)
          if (!urlMatch) return
          const [, owner, repo, number] = urlMatch
          if (!owner || !repo || !number) return

          const currentUserLogin = getCurrentUserLogin()
          const variables = JSON.stringify({count: 15, hideTimeline: false, number: Number(number), owner, repo})
          const cacheKey = `relay-query-${currentUserLogin}-IssueViewerViewQuery#${variables}`

          const cachedQuery = await storage?.getItem(cacheKey)
          if (cachedQuery) continue

          const response = await fetch(`/${owner}/${repo}/issues/preheat?number=${number}&source=pulls`, {
            headers: {
              Accept: 'application/json',
            },
          })
          if (response.status === 200) {
            const payload = await response.json()
            await storage?.setItem(cacheKey, payload.cacheValue)
          } else {
            continue
          }

          noIssuesToPreheat = false
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          // Ignore errors and continue preheating other queries
        }
      }
      if (noIssuesToPreheat) {
        pendingRef.current = false
      }
    }

    const scheduleNextPreheat = () => {
      if (cancelled || !('requestIdleCallback' in window)) return

      // eslint-disable-next-line compat/compat
      requestIdleCallback(() => {
        if (cancelled || !pendingRef.current) return
        const runPreheat = async () => {
          await preheatQueries()
          scheduleNextPreheat()
        }
        void runPreheat()
      })
    }

    // Replay any buffered events that fired before we registered.
    // __preheatEventBuffer is declared globally in issue-link.ts
    const buffer = (window as Window & {__preheatEventBuffer?: Array<{href?: string}>}).__preheatEventBuffer
    if (buffer && buffer.length > 0) {
      for (const detail of buffer) {
        if (detail.href) {
          addIssue(detail.href)
        }
      }
      // Clear the buffer so events are not processed twice on re-mount.
      buffer.length = 0
    }

    const handleEvent = (e: Event) => {
      addIssue((e as CustomEvent).detail.href)
    }

    window.addEventListener('issue-link-to-preheat', handleEvent)
    return () => {
      cancelled = true
      window.removeEventListener('issue-link-to-preheat', handleEvent)
    }
  }, [enabled, storage])
}
